import React from "react";
import { useQuery } from "@tanstack/react-query";

import { getCurrentUser } from "~/api";
import { ROUTES } from "~/router";
import { useLogout } from "~/screens/Auth/hooks/useLogout";
import { ROLES } from "~/shared.types";
import { useAuthStore } from "~/stores";
import { customIcons, icons } from "~/ui";

export interface NavBarProps {
  setSidebarOpen?: (v: boolean) => void;
  canLogout: boolean;
  children?: React.ReactNode;
}

const HUB_ACCESS_ROLES: readonly string[] = [
  ROLES.ADMIN.value,
  ROLES.SUPER_ADMIN.value,
];

export const Navbar = ({
  setSidebarOpen,
  canLogout,
  children,
}: NavBarProps) => {
  const { mutate: logout } = useLogout();
  const accessToken = useAuthStore((state) => state.accessToken);
  const { data: currentUser } = useQuery({
    queryKey: ["getCurrentUser"],
    queryFn: getCurrentUser,
    enabled: !!accessToken,
  });
  const logoHref =
    currentUser && HUB_ACCESS_ROLES.includes(currentUser.userType)
      ? ROUTES.hubHome
      : ROUTES.dashboard;

  return (
    <div className="h-18 border-blueGrey lg:px-15 sticky top-0 z-10 flex shrink-0 items-center border-b bg-white px-4 sm:px-6">
      {/* mobile: hamburger left, logo absolutely centered */}
      <div className="flex flex-1 items-center lg:hidden">
        {setSidebarOpen && (
          <button
            type="button"
            className="text-violet -m-2.5 p-2.5"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <icons.outline.Bars3Icon aria-hidden="true" className="size-6" />
          </button>
        )}
      </div>

      <a
        href={logoHref}
        className="absolute left-1/2 -translate-x-1/2 cursor-pointer lg:static lg:translate-x-0"
      >
        <customIcons.LogoIcon className="w-44 lg:w-56" />
      </a>

      <div className="flex flex-1 items-center justify-end gap-x-4 sm:gap-x-6">
        {children}
        {canLogout && (
          <button
            role="button"
            aria-label="Logout"
            title="Logout"
            className="hidden cursor-pointer lg:block"
            onClick={() => logout()}
          >
            <icons.outline.ArrowLeftEndOnRectangleIcon className="text-vivid-blue-purple size-6" />
          </button>
        )}
      </div>
    </div>
  );
};
